import { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useCycles } from '../../../api/hooks';
import { useAuth } from '../../../../utils/Auth/AuthProvider';
import { EmptyState, Chip, PagedFooter, PanelCard, Track } from '../../../components/ui';
import Spinner from '../../../components/ui/Spinner';
import ErrorPanel from '../../../components/ui/ErrorPanel';
import { FaClipboardList } from '../../../components/ui/icons';
import { LabelledSteps } from '../components/cycleBits';
import CycleDetail from '../components/CycleDetail';
import { usePagination } from '../../../lib/usePagination';
import { dayMonth, signed1, deltaTone } from '../lib/cpBits';

interface Props { gradeId: number; sectionId: number | 'all'; subjectId: number | 'all' }

const LABEL = 'font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';

/** Design CP "Remediation cycles": newest first, one accordion card per cycle, the before/after table inside the open one. */
export default function CyclesTab({ gradeId, sectionId, subjectId }: Props) {
  const { role } = useAuth();
  const canClose = role === 'teacher' || role === 'school_admin' || role === 'school' || role === 'admin' || role === 'superuser';
  const [openId, setOpenId] = useState<number | null>(null);
  const q = useCycles({ grade: gradeId, section: sectionId, subject: subjectId });
  const rows = useMemo(
    () => [...(q.data?.cycles ?? [])].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [q.data],
  );
  const pg = usePagination(rows, 10);
  if (q.isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (q.isError || !q.data) return <ErrorPanel />;
  if (q.data.cycles.length === 0) {
    return (
      <PanelCard>
        <EmptyState icon={<FaClipboardList />} title="No remediation cycles yet"
          body="Launch one from an exam report or the Chapters heatmap." />
      </PanelCard>
    );
  }
  const gains = q.data.cycles.map((c) => c.gain.summary?.mean_delta).filter((v): v is number => v != null);
  const meanGain = gains.length > 0 ? gains.reduce((a, b) => a + b, 0) / gains.length : null;
  const inProgress = q.data.cycles.filter((c) => c.status !== 'closed').length;
  return (
    <PanelCard title="Remediation cycles" help="cycle" align="end"
      subtitle="Open a cycle to see each student's before and after."
      actions={(
        <>
          {meanGain != null && <Chip tone={deltaTone(meanGain)}>{signed1(meanGain)} pts mean gain</Chip>}
          <Chip tone="neutral">{inProgress} in progress</Chip>
        </>
      )}>
      {pg.pageItems.map((c) => {
        const s = c.gain.summary;
        const isOpen = openId === c.id;
        const share = s?.crossed_pass_share;
        const gain = s?.mean_delta;
        return (
          <div key={c.id} className={`mb-3 overflow-hidden rounded-[16px] border bg-surface ${isOpen ? 'border-on-navy-muted shadow-card-hover' : 'border-line'}`}>
            <button type="button" aria-expanded={isOpen} onClick={() => setOpenId(isOpen ? null : c.id)}
              className="flex w-full flex-wrap items-center gap-x-6 gap-y-3.5 px-[18px] py-4 text-left hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              <span aria-hidden className="grid h-[46px] w-[46px] flex-none place-items-center rounded-[13px] bg-mist font-mono text-[13px] font-semibold text-muted-2">#{c.id}</span>
              <span className="min-w-0 flex-[1_1_220px]">
                <span className="block font-semibold">{c.source_exam?.name ?? 'Chapter heatmap'}</span>
                <span className="text-xs text-muted">{c.target_chapters.map((t) => t.name).join(', ')} · created {dayMonth(c.created_at)}</span>
              </span>
              <LabelledSteps status={c.status} stepWidth={70} />
              <span className="flex min-w-[70px] flex-col gap-1">
                <span className={LABEL}>Students</span>
                <span className="font-mono text-base font-semibold">{c.target_count}</span>
              </span>
              <span className="flex min-w-[70px] flex-col gap-1">
                <span className={LABEL}>Mean gain</span>
                <span className={`font-mono text-base font-semibold ${gain == null ? 'text-muted' : deltaTone(gain) === 'up' ? 'text-success' : deltaTone(gain) === 'down' ? 'text-danger-ink' : 'text-muted'}`}>{gain == null ? '—' : signed1(gain)}</span>
              </span>
              <span className="flex w-[150px] flex-col gap-1.5">
                <span className={LABEL}>Crossed pass · {share == null ? '—' : `${Math.round(share * 100)}%`}</span>
                <Track value={share == null ? 0 : share * 100} tone="slate" label={`Cycle ${c.id} crossed pass`} />
              </span>
              <ChevronRight size={18} strokeWidth={2} aria-hidden className={`flex-none text-muted transition-transform ${isOpen ? 'rotate-90' : ''}`} />
            </button>
            {isOpen && (
              <div className="border-t border-hairline bg-surface-2 p-[18px]">
                <CycleDetail cycle={c} canClose={canClose} />
              </div>
            )}
          </div>
        );
      })}
      <PagedFooter className="mt-3.5" label="Cycles pagination" {...pg} onPage={pg.setPage} />
    </PanelCard>
  );
}
